import React from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';
import type { DashboardAlert } from '../types/dashboard';
import { alertPath } from '../utils/dashboardPaths';
import { formatDeadlineAlertMessage } from '@features/report/tag/utils/reportTagDeadline';
import { DASHBOARD_ALERT_TYPES } from '../constants/dashboardAlerts';

export const DASHBOARD_REPORTING_CHECKLIST_ID = 'dashboard-reporting-checklist';

interface Props {
  alerts: DashboardAlert[];
  scope: string;
  scopeId: number;
}

function resolveAlertMessage(alert: DashboardAlert): string {
  if (
    alert.type === DASHBOARD_ALERT_TYPES.DEADLINE_URGENT ||
    alert.type === DASHBOARD_ALERT_TYPES.DEADLINE_PASSED
  ) {
    return (
      formatDeadlineAlertMessage({
        type: alert.type,
        period_name: alert.period_name,
        deadline: alert.deadline,
        days_until_deadline: alert.days_until_deadline,
      }) ?? alert.message ?? 'Deadline attention required'
    );
  }

  return alert.message ?? 'Attention required';
}

function isDeadlineAlert(type: string): boolean {
  return (
    type === DASHBOARD_ALERT_TYPES.DEADLINE_URGENT ||
    type === DASHBOARD_ALERT_TYPES.DEADLINE_PASSED
  );
}

function scrollToReportingChecklist(): void {
  const el = document.getElementById(DASHBOARD_REPORTING_CHECKLIST_ID);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const DashboardAlerts: React.FC<Props> = ({ alerts, scope, scopeId }) => {
  const navigate = useNavigate();

  if (!alerts.length) return null;

  return (
    <div className="dashboard-alerts d-flex flex-column gap-2 mb-4">
      {alerts.map((alert, idx) => {
        const variant = alert.severity === 'danger' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info';
        const Icon = variant === 'info' ? FiInfo : FiAlertTriangle;
        const path = alertPath(alert.type, scope, scopeId);
        const message = resolveAlertMessage(alert);
        const showViewChecklist = isDeadlineAlert(alert.type);

        return (
          <Alert
            key={`${alert.type}-${idx}`}
            variant={variant}
            className="d-flex align-items-center justify-content-between mb-0 py-2"
          >
            <div className="d-flex align-items-center gap-2 min-w-0 me-2">
              <Icon size={18} className="flex-shrink-0" />
              <span>{message}</span>
            </div>
            {showViewChecklist ? (
              <Button
                size="sm"
                variant={`outline-${variant}`}
                className="flex-shrink-0 text-nowrap"
                onClick={scrollToReportingChecklist}
              >
                View checklist
              </Button>
            ) : path ? (
              <Button
                size="sm"
                variant={`outline-${variant}`}
                className="flex-shrink-0 text-nowrap"
                onClick={() => navigate(path)}
              >
                View
              </Button>
            ) : null}
          </Alert>
        );
      })}
    </div>
  );
};

export default DashboardAlerts;
